import { PDFDocument, PDFFont, rgb } from 'pdf-lib';
import { embedBodyFonts, embedStandardFallbackFonts } from './pdfFonts';
import type { ImageFitMode, PdfExportQuality, PdfQualityLayout } from './pdfExportQuality';
import { layoutForQuality } from './pdfExportQuality';
import { renderHtmlEmailRasterFallback, renderHtmlEmailVector, type HtmlPdfCursor } from './renderEmailHtmlToPdf';
import type { PageDims } from './pdfPageDims';

function drawFooter(page: ReturnType<PDFDocument['getPage']>, index: number, total: number, font: PDFFont, margin: number): void {
  const label = `Page ${index + 1} of ${total}`;
  const size = 8.5;
  const w = font.widthOfTextAtSize(label, size);
  const pw = page.getWidth();
  page.drawText(label, {
    x: pw - margin - w,
    y: margin * 0.55,
    size,
    font,
    color: rgb(0.55, 0.55, 0.58),
  });
}

function applyFooters(doc: PDFDocument, font: PDFFont, margin: number): void {
  const pages = doc.getPages();
  pages.forEach((p, i) => drawFooter(p, i, pages.length, font, margin));
}

export interface HtmlStringPdfOptions {
  title?: string;
  quality: PdfExportQuality;
  pageDims: PageDims;
  imageFit: ImageFitMode;
  useRasterHtmlFallback: boolean;
  /** Narrow/normal/wide margin multiplier around layout.margin */
  marginPresetFactor: number;
}

export async function convertHtmlFragmentToPdf(htmlBodyInner: string, opts: HtmlStringPdfOptions): Promise<Uint8Array> {
  const baseLayout = layoutForQuality(opts.quality);
  const layout: PdfQualityLayout = {
    ...baseLayout,
    margin: baseLayout.margin * opts.marginPresetFactor,
  };

  const doc = await PDFDocument.create();
  let fonts;
  try {
    fonts = await embedBodyFonts(doc);
  } catch {
    fonts = await embedStandardFallbackFonts(doc);
  }

  const { widthPt, heightPt } = opts.pageDims;
  const page = doc.addPage([widthPt, heightPt]);
  let y = heightPt - layout.margin;

  if (opts.title) {
    const lines = wrapTitle(opts.title, fonts.bold, layout.subjectSize, widthPt - 2 * layout.margin);
    const lh = layout.subjectSize * 1.15;
    for (const ln of lines) {
      page.drawText(ln, {
        x: layout.margin,
        y,
        size: layout.subjectSize,
        font: fonts.bold,
        color: rgb(0.08, 0.09, 0.12),
      });
      y -= lh + 2;
    }
    y -= 8;
  }

  const htmlC: HtmlPdfCursor = {
    doc,
    page,
    y,
    fonts,
    layout,
    pageWidthPt: widthPt,
    pageHeightPt: heightPt,
  };

  if (opts.useRasterHtmlFallback) {
    await renderHtmlEmailRasterFallback(htmlC, htmlBodyInner);
  } else {
    await renderHtmlEmailVector(htmlC, htmlBodyInner, opts.imageFit, async () => {
      await new Promise<void>((r) => window.requestAnimationFrame(() => r()));
    });
  }

  applyFooters(doc, fonts.regular, layout.margin);
  return doc.save();
}

function wrapTitle(text: string, font: PDFFont, size: number, maxW: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const trial = line ? `${line} ${w}` : w;
    if (font.widthOfTextAtSize(trial, size) <= maxW) line = trial;
    else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}
